import envConfig from "../../envConfig";

// Region-aware base URL of the ToothFairyAI web application.
//
// The desktop (Electron) app redirects purchase / billing flows to the web app
// rather than running Stripe hosted flows in-app (Apple Pay, 3DS, bank
// redirects and wallet returns are unreliable inside a Chromium BrowserWindow,
// and there is no deep-link back to the desktop shell). The web app is deployed
// per region, so the redirect target must match the region the desktop client
// is logged into — sending a US user to the AU domain would land them on the
// wrong tenant.
//
// Regions (from the build .env files):
//   aus -> https://app.toothfairyai.com        (AU / default)
//   eu  -> https://app.eu.toothfairyai.com
//   us  -> https://app.us.toothfairyai.com
//   dev -> https://app.toothfairylab.link
export const getWebAppBaseUrl = () => {
  if (envConfig?.REACT_APP_ENV === "dev") {
    return "https://app.toothfairylab.link";
  }
  const region = envConfig?.REACT_APP_REGION;
  if (region === "eu" || region === "us") {
    return `https://app.${region}.toothfairyai.com`;
  }
  return "https://app.toothfairyai.com";
};

// Build a fully-qualified web-app URL for `path` (a route starting with "/" or
// "" for the root). Never append a trailing slash to the base.
export const getWebAppUrl = (path = "") => `${getWebAppBaseUrl()}${path}`;

// DEV ONLY override so region-gated features (e.g. voice agents) can be tested
// without editing the .env files. In a web/Electron dev build, set:
//   localStorage.setItem("tf_region_override", "eu")
// then reload. Set to "aus" (or remove the key) to switch back.
const getRegionOverride = () => {
  if (envConfig?.REACT_APP_ENV !== "dev") return null;
  if (typeof window === "undefined") return null;
  try {
    return window?.localStorage?.getItem("tf_region_override") || null;
  } catch (e) {
    return null;
  }
};

// Effective region the current build is running as. For local/dev testing the
// region can be faked via the `tf_region_override` localStorage key (see above).
export const getEffectiveRegion = () =>
  getRegionOverride() || envConfig?.REACT_APP_REGION;

// Voice agents (mode: "voice") are only available in the AU (default) region
// today. The EU and US deployments are not live yet, so surfaces that offer the
// voice mode must hide it and show the user a notice instead.
export const isVoiceAgentsAvailableInRegion = () => {
  const region = getEffectiveRegion();
  return region !== "eu" && region !== "us";
};

// Live mode (real-time voice segments inside a live session) depends on the
// LiveKit voice backend, which is only deployed in the AU (default) region for
// now. EU/US users can still chat (text) in a live session, but the "Start
// Live Mode" entry points must render disabled with a notice — even when the
// voice generation itself is enabled.
export const isLiveModeAvailableInRegion = () => {
  const region = getEffectiveRegion();
  return region !== "eu" && region !== "us";
};

export default {
  getWebAppBaseUrl,
  getWebAppUrl,
  getEffectiveRegion,
  isVoiceAgentsAvailableInRegion,
  isLiveModeAvailableInRegion,
};
